import { createFileThunk } from '../../redux/thunks/file';
import CustomInput from '../reusable/input';
import FileItemComponent from './file_component';
import { gray } from '../../theme/themePrimitives';
import { settingSelectedFolder } from '../../redux/slices/folder';
import StartANewDocComponent from './start_a_doc';
import { useParams } from 'react-router';
import { AddOutlined, UploadFile } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { CreateFileParams, FileObject } from '../../params/redux/file';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';


const SelectedFolderComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [search, setSearch] = useState<string>('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [startingADoc, setStartingADoc] = useState<boolean>(false);
    const open = Boolean(anchorEl);
    const files = useSelector((state: RootState) => state.file.files, (prevFiles, nextFiles) => JSON.stringify(prevFiles) === JSON.stringify(nextFiles));
    const { selectedFolder } = useSelector((state: RootState) => state.folder);

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleApiCall = async (fileBase64: string, file: File) => {
        try {
            const fileData: CreateFileParams = {
                client_id: id ? parseInt(id as string, 10) : 0,
                file: fileBase64,
                filename: file.name,
                folder_id: selectedFolder?.id.toString() || '0'
            };
            await dispatch(createFileThunk(fileData)).unwrap();
        } catch (error) {
            // Catch errors
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (reader.result) {
                    resolve(reader.result as string);
                } else {
                    reject(new Error('File reading failed.'));
                }
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const fileBase64: string = await fileToBase64(file);
        await handleApiCall(fileBase64, file);
    };

    const handleFileSelect = () => {
        handleMenuClose();
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const startNewDocHandler = () => {
        handleMenuClose();
        setStartingADoc(true);
    };

    if (startingADoc) {
        return <StartANewDocComponent setStartingADoc={setStartingADoc} />;
    }

    return (
        <Box borderTop={`1px solid ${gray[200]}`} py={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Button variant="text" color="secondary" onClick={() => dispatch(settingSelectedFolder(null))}>&larr; Back</Button>
                <Typography variant="h5" color="info">{selectedFolder?.name}</Typography>
                <Box></Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <CustomInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                />
                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleButtonClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px'
                        }}
                    >
                        + Add
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        MenuListProps={{
                            'aria-labelledby': 'add-pipeline-button'
                        }}
                    >
                        <MenuItem onClick={startNewDocHandler}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">
                                <AddOutlined color="disabled" />
                                Start a new doc
                            </Box>
                        </MenuItem>
                        <MenuItem onClick={handleFileSelect}>
                            <Box color={gray[500]} display="flex" alignItems="center" gap="10px">
                                <UploadFile color="disabled" />
                                Upload files
                            </Box>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" overflow="auto" py={1}>
                {files.map((file: FileObject, index) => (
                    <Box key={index}>
                        <FileItemComponent file={file} />
                    </Box>
                ))}
            </Box>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept="*/*"
            />
        </Box>
    );
};

export default SelectedFolderComponent;
